import { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  useBudgetPreview,
  useBudgetImportConfirm,
  type BudgetPreviewLine,
} from '../hooks/useBudgetImport';

type Step = 'upload' | 'preview' | 'result';

function formatCurrency(value: number | null): string {
  if (value == null) return '\u2014';
  return (-value).toLocaleString('nb-NO', { maximumFractionDigits: 0 }) + ' kr';
}

const previewColumns: GridColDef[] = [
  { field: 'ansvar', headerName: 'Ansvar', width: 80 },
  { field: 'ansvarNavn', headerName: 'Ansvarsnavn', width: 180 },
  { field: 'konto', headerName: 'Konto', width: 80 },
  { field: 'kontoNavn', headerName: 'Kontonavn', width: 180 },
  { field: 'tjenester', headerName: 'Tjenester', width: 120 },
  {
    field: 'budsjettBeloep',
    headerName: 'Budsjett (helår)',
    width: 140,
    type: 'number',
    valueFormatter: (value: number) => formatCurrency(value),
  },
  { field: 'antallRader', headerName: 'Rader', width: 70, type: 'number' },
];

export function BudgetImportPage() {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [previewData, setPreviewData] = useState<{
    filename: string;
    totalRowsInSheet: number;
    filteredRows: number;
    aggregatedRows: number;
    zeroBeloepSkipped: number;
    aar: number;
    totalBudsjett: number;
    preview: BudgetPreviewLine[];
  } | null>(null);
  const [importResult, setImportResult] = useState<{
    createdLines: number;
    updatedLines: number;
    totalBudsjett: number;
    aar: number;
  } | null>(null);

  const previewMutation = useBudgetPreview();
  const importMutation = useBudgetImportConfirm();

  const handleFile = useCallback((f: File) => {
    setFile(f);
    previewMutation.mutate(f, {
      onSuccess: (resp) => {
        setPreviewData(resp.data);
        setStep('preview');
      },
    });
  }, [previewMutation]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handleConfirm = () => {
    if (!file) return;
    importMutation.mutate(file, {
      onSuccess: (resp) => {
        setImportResult(resp.data);
        setStep('result');
      },
    });
  };

  return (
    <Box>
      <Typography variant="h2" sx={{ mb: 1 }}>Budsjettimport</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Last opp en Visma-eksport (.xlsx) med budsjettlinjer. Kun kontoer 16000–18000 importeres (ekskl. 17290).
      </Typography>

      {step === 'upload' && (
        <Paper
          variant="outlined"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={{
            p: 6, textAlign: 'center', border: 2, borderStyle: 'dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            bgcolor: dragOver ? 'primary.50' : 'background.default',
            transition: 'all 0.2s',
          }}
        >
          {previewMutation.isPending ? (
            <Box>
              <CircularProgress size={32} sx={{ mb: 1 }} />
              <Typography variant="body2">Leser regneark...</Typography>
            </Box>
          ) : (
            <>
              <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Dra og slipp en .xlsx-fil her, eller
              </Typography>
              <Button variant="contained" component="label">
                Velg fil
                <input type="file" hidden accept=".xlsx,.xls" onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                }} />
              </Button>
            </>
          )}
        </Paper>
      )}

      {previewMutation.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {previewMutation.error instanceof Error ? previewMutation.error.message : 'Kunne ikke lese regnearket'}
        </Alert>
      )}

      {step === 'preview' && previewData && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>{previewData.filename}</strong> — {previewData.totalRowsInSheet} rader i arket,{' '}
            {previewData.filteredRows} etter kontofilter,{' '}
            <strong>{previewData.aggregatedRows}</strong> unike inntektslinjer etter aggregering.
            {previewData.zeroBeloepSkipped > 0 && <> ({previewData.zeroBeloepSkipped} med 0 i budsjett utelatt)</>}
            {' '}År: <strong>{previewData.aar}</strong>.
            Totalbudsjett: <strong>{formatCurrency(previewData.totalBudsjett)}</strong>
          </Alert>

          <DataGrid
            rows={previewData.preview}
            columns={previewColumns}
            getRowId={(row) => row.inntektslinjeID}
            autoHeight
            pageSizeOptions={[25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
            disableRowSelectionOnClick
            sx={{ bgcolor: 'background.paper', mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => { setStep('upload'); setPreviewData(null); setFile(null); }}>
              Tilbake
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? 'Importerer...' : `Importer ${previewData.aggregatedRows} linjer`}
            </Button>
          </Box>

          {importMutation.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {importMutation.error instanceof Error ? importMutation.error.message : 'Import feilet'}
            </Alert>
          )}
        </Box>
      )}

      {step === 'result' && importResult && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ '& .MuiAlert-message': { width: '100%' } }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Import fullført</Typography>
          <Typography variant="body2">År: {importResult.aar}</Typography>
          <Typography variant="body2">Opprettet: {importResult.createdLines} inntektslinjer</Typography>
          <Typography variant="body2">Oppdatert: {importResult.updatedLines} inntektslinjer</Typography>
          <Typography variant="body2">Totalbudsjett: {formatCurrency(importResult.totalBudsjett)}</Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button variant="contained" size="small" onClick={() => { setStep('upload'); setPreviewData(null); setFile(null); setImportResult(null); }}>
              Importer ny fil
            </Button>
            <Button variant="outlined" size="small" href="/income-lines">
              Gå til inntektslinjer
            </Button>
          </Box>
        </Alert>
      )}
    </Box>
  );
}
